import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { getAuthorizationApi } from "./api";
import { getToken, useLogin } from "./authConfig";
import { useAuthContext } from "./loginContext";

interface AuthorizationContextType {
    isAdmin: boolean;
    loading: boolean;
    error: string | null;
}

const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined);

export const AuthorizationProvider = ({ children }: { children: ReactNode }) => {
    const { instance: client } = useMsal();
    const { authLoading, isLoggedIn } = useAuthContext();
    const [adminAuthorized, setAdminAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const fetchAttemptRef = useRef(0);

    useEffect(() => {
        const currentAttempt = ++fetchAttemptRef.current;

        if (authLoading) {
            setLoading(true);
            return;
        }

        if (!useLogin || !isLoggedIn) {
            setAdminAuthorized(false);
            setError(null);
            setLoading(false);
            return;
        }

        const controller = new AbortController();

        const fetchAuthorization = async () => {
            setAdminAuthorized(false);
            setError(null);
            setLoading(true);

            try {
                const token = await getToken(client);
                if (!token) {
                    throw new Error("No authentication token available");
                }

                if (controller.signal.aborted || currentAttempt !== fetchAttemptRef.current) {
                    return;
                }

                const authorization = await getAuthorizationApi(token, controller.signal);
                if (controller.signal.aborted || currentAttempt !== fetchAttemptRef.current) {
                    return;
                }

                setAdminAuthorized(authorization.isAdmin === true);
            } catch (err) {
                if (controller.signal.aborted || currentAttempt !== fetchAttemptRef.current) {
                    return;
                }

                console.error("Error loading authorization:", err);
                setAdminAuthorized(false);
                setError(err instanceof Error ? err.message : "Failed to load authorization");
            } finally {
                if (!controller.signal.aborted && currentAttempt === fetchAttemptRef.current) {
                    setLoading(false);
                }
            }
        };

        void fetchAuthorization();

        return () => controller.abort();
    }, [authLoading, client, isLoggedIn]);

    const authorizationLoading = loading || authLoading;

    return (
        <AuthorizationContext.Provider value={{ isAdmin: !authorizationLoading && isLoggedIn && adminAuthorized, loading: authorizationLoading, error }}>
            {children}
        </AuthorizationContext.Provider>
    );
};

export const useAuthorization = () => {
    const context = useContext(AuthorizationContext);
    if (!context) {
        throw new Error("useAuthorization must be used within an AuthorizationProvider");
    }
    return context;
};
